import { useEffect } from "react";
import { api, type User } from "./api.js";
import { patchSession } from "./session.js";
import { createStore, useStore } from "./store.js";

export interface ProfileDraft {
  displayName: string;
  avatarId: string;
}

export type ProfileSaveStatus = "idle" | "saving" | "saved" | "error";

const editorStore = createStore<{ userId: string | null; draft: ProfileDraft; saving: boolean; status: ProfileSaveStatus; error: unknown }>({
  userId: null, draft: { displayName: "", avatarId: "avatar-01" }, saving: false, status: "idle", error: null,
});

/** A single memory-only editor survives automatic shell changes, scoped to the account. */
export function useProfileEditor(user: User | null) {
  const { draft, saving, status, error } = useStore(editorStore);
  const setDraft = (update: ProfileDraft | ((current: ProfileDraft) => ProfileDraft)) => editorStore.set((state) => ({ ...state, draft: typeof update === "function" ? update(state.draft) : update }));
  const setSaving = (saving: boolean) => editorStore.set((state) => ({ ...state, saving }));
  const setStatus = (status: ProfileSaveStatus) => editorStore.set((state) => ({ ...state, status }));
  const setError = (error: unknown) => editorStore.set((state) => ({ ...state, error }));
  useEffect(() => {
    if (user === null) return;
    if (editorStore.get().userId !== user.id) {
      editorStore.set({ userId: user.id, draft: { displayName: user.displayName, avatarId: user.avatarId }, saving: false, status: "idle", error: null });
    }
  }, [user?.id]);

  const dirty = user !== null && (
    draft.displayName.trim() !== user.displayName || draft.avatarId !== user.avatarId
  );

  function setDisplayName(displayName: string): void {
    setDraft((current) => ({ ...current, displayName }));
    setStatus("idle");
    setError(null);
  }

  function setAvatarId(avatarId: string): void {
    setDraft((current) => ({ ...current, avatarId }));
    setStatus("idle");
    setError(null);
  }

  async function save(): Promise<{ ok: true } | { ok: false; error: unknown }> {
    if (user === null || !dirty || saving) return { ok: false, error: new Error("profile_not_ready") };
    setSaving(true);
    setStatus("saving");
    setError(null);
    try {
      const updated = await api.updateProfile({
        displayName: draft.displayName.trim(),
        avatarId: draft.avatarId,
      });
      if (editorStore.get().userId !== user.id) return { ok: false, error: new Error("account_changed") };
      setDraft({ displayName: updated.user.displayName, avatarId: updated.user.avatarId });
      patchSession({ user: updated.user });
      setStatus("saved");
      return { ok: true };
    } catch (caught) {
      if (editorStore.get().userId === user.id) { setStatus("error"); setError(caught); }
      return { ok: false, error: caught };
    } finally {
      if (editorStore.get().userId === user.id) setSaving(false);
    }
  }

  return { draft, setDisplayName, setAvatarId, dirty, saving, status, error, save };
}
